/**
 * Mission fields that ride along on a copied task: docs, ticks, uploads,
 * variable values, and host anchors. Ids inside the copied set become
 * array indexes; everything else is copied by reference or dropped.
 */

import { omitEmpty } from '../constants';
import { getWaitSeconds } from '../../../../shared/ConditionsDropdown/utils/conditionWait';
import { DEFAULT_OPTIONS } from '../../../TaskNode/DecisionOptionsModal';

/**
 * Index form of a dependsOn entry. Wait rides on the object when present so
 * paste can restore a time gate that lived on the edge (legacy / convert path).
 */
export function serializeDependsOnIndex(dep, depIndex, resolveDepTask) {
  const waitSeconds = typeof dep === 'object' ? getWaitSeconds(dep) : null;
  if (typeof dep === 'object' && dep.branch) {
    const depTask = resolveDepTask();
    const depOptions = depTask?.options?.length > 0 ? depTask.options : DEFAULT_OPTIONS;
    const branchIdx = depOptions.findIndex(o => o.id === dep.branch);
    if (branchIdx >= 0) {
      return omitEmpty({ task: depIndex, branch: branchIdx, waitSeconds });
    }
  }
  if (waitSeconds) {
    return omitEmpty({ task: depIndex, waitSeconds });
  }
  return depIndex;
}

/**
 * Attached docs copy BY REFERENCE (doc ids are workspace-global), matching
 * template copy and Ctrl+D duplicate. The title/icon snapshots ride along so
 * a paste where the doc no longer exists can still show "Title — Deleted".
 */
export function serializeAttachedDocs(task) {
  const entries = (Array.isArray(task.attachedDocs) ? task.attachedDocs : [])
    .filter(e => e && typeof e.docId === 'string' && e.docId)
    .map(e => omitEmpty({ docId: e.docId, title: e.title, icon: e.icon }));
  return entries.length > 0 ? entries : undefined;
}

/**
 * Milestones attach to their host mission via `milestoneAnchor: { hostTaskId, side }`
 * — that anchor (not dependsOn) drives their placement and edges. Serialize the
 * host as an array index like dependencies. If the host isn't in the copied set,
 * omit the anchor: the milestone pastes as a free-floating marker.
 */
export function serializeMilestoneAnchor(task, taskIdToIndex) {
  if (task.nodeType !== 'milestone') return undefined;
  const anchor = task.milestoneAnchor;
  if (!anchor?.hostTaskId || (anchor.side !== 'input' && anchor.side !== 'output')) return undefined;
  const hostIndex = taskIdToIndex.get(anchor.hostTaskId);
  if (hostIndex === undefined) return undefined;
  return { task: hostIndex, side: anchor.side };
}

export function serializeNoteAnchor(task, taskIdToIndex) {
  if (task.nodeType !== 'note') return undefined;
  const hostTaskId = task.noteAnchor?.hostTaskId;
  if (!hostTaskId) return undefined;
  const hostIndex = taskIdToIndex.get(hostTaskId);
  if (hostIndex === undefined) return undefined;
  return { task: hostIndex };
}

/**
 * Per-mission checkbox overrides for attached docs, carried through like
 * Ctrl+D duplicate: paste never rewrites doc block ids, so ticks stay valid.
 * Ticks for docs that are no longer attached are stale working state — drop.
 */
export function serializeDocTicks(task, serializedAttachedDocs) {
  if (!serializedAttachedDocs || !task.docTicks || typeof task.docTicks !== 'object') return undefined;
  const attachedIds = new Set(serializedAttachedDocs.map(e => e.docId));
  const ticks = {};
  for (const [docId, overrides] of Object.entries(task.docTicks)) {
    if (!attachedIds.has(docId)) continue;
    if (!overrides || typeof overrides !== 'object' || Object.keys(overrides).length === 0) continue;
    ticks[docId] = overrides;
  }
  return Object.keys(ticks).length > 0 ? ticks : undefined;
}

export function serializeDocFiles(task, serializedAttachedDocs) {
  if (!serializedAttachedDocs || !task.docFiles || typeof task.docFiles !== 'object') return undefined;
  const attachedIds = new Set(serializedAttachedDocs.map(e => e.docId));
  const files = {};
  for (const [docId, overrides] of Object.entries(task.docFiles)) {
    if (!attachedIds.has(docId)) continue;
    if (!overrides || typeof overrides !== 'object' || Array.isArray(overrides)) continue;
    const next = {};
    for (const [blockId, list] of Object.entries(overrides)) {
      if (!Array.isArray(list) || list.length === 0) continue;
      next[blockId] = list
        .filter((f) => f && f.attachmentId)
        .map((f) => {
          const row = {
            attachmentId: f.attachmentId,
            filename: f.filename,
            mimeType: f.mimeType,
            size: f.size,
            fileType: f.fileType,
            uploadedAt: f.uploadedAt,
          };
          if (typeof f.thumbDataUrl === 'string' && f.thumbDataUrl.startsWith('data:image/')) {
            row.thumbDataUrl = f.thumbDataUrl;
          }
          return row;
        });
      if (next[blockId].length === 0) delete next[blockId];
    }
    if (Object.keys(next).length) files[docId] = next;
  }
  return Object.keys(files).length > 0 ? files : undefined;
}

export function serializeVariableValues(task) {
  if (!Array.isArray(task?.variableValues) || task.variableValues.length === 0) return undefined;
  return task.variableValues.map((row) => ({
    ...row,
    values: Array.isArray(row.values) ? [...row.values] : row.values,
  }));
}
